
if (!customElements.get('custom-pagination')) {
  class CustomPagination extends HTMLElement {
    constructor() {
      super();
      this.sectionId = this.dataset.section;
      this.productList = document.querySelector(`ul[data-id="${this.sectionId}"]`);
      this.loadMoreButton = this.querySelector('.btn--load-more');
      this.paginationType = this.getAttribute('data-type');
      this.countItem = parseInt(this.productList.getAttribute('data-products-count'), 10);
      this.currentPage = 2;
      this.scrollObserver = null;
    }

    connectedCallback() {
      this.paginationType === 'load-more-button'
        ? this.setupLoadMoreButton()
        : this.paginationType === 'infinite-scroll'
        ? this.setupInfiniteScroll()
        : null;

      this.restoreScrollState();
      this.bindProductLinks();

      // Safari occasionally keeps the page in bfcache. In that case connectedCallback
      // does not run again, but this listener (kept alive with the page) still fires.
      window.addEventListener('pageshow', event => {
        if (event.persisted) this.restoreScrollState();
      });
    }

    bindProductLinks() {
      this.productList?.querySelectorAll('.grid__item a').forEach(link => {
        if (link.dataset.paginationBound) return; // avoid stacking duplicate listeners on re-bind
        link.dataset.paginationBound = 'true';
        link.addEventListener('click', () => {
          const item = link.closest('.grid__item');
          const itemHref = item?.querySelector('a[href]')?.getAttribute('href') || link.getAttribute('href') || '';
          sessionStorage.setItem('pagination-scrollY', window.scrollY);
          sessionStorage.setItem('pagination-currentPage', this.currentPage - 1);
          sessionStorage.setItem('pagination-itemHref', itemHref);
        });
      });
    }

    setupLoadMoreButton() {
      this.loadMoreButton?.addEventListener('click', () => this.loadNextPage());
    }

    setupInfiniteScroll() {
      this.scrollObserver = new IntersectionObserver(
        entries => entries[0].isIntersecting && this.loadNextPage(),
        { threshold: [0, 1] }
      );
      this.scrollObserver.observe(this);
    }

    buildUrlWithPageNumber(search = '', key = 'page') {
      const urlParams = new URLSearchParams(search);
      urlParams.set(key, this.currentPage);
      return `?${urlParams.toString()}`;
    }

    async loadNextPage() {
      if (this.hasAttribute('loading')) return;

      try {
        this.setAttribute('loading', true);
        this.toggleLoadingSpinner(true);

        const url = `${document.location.pathname}${this.buildUrlWithPageNumber(document.location.search)}`;
        const response = await fetch(url);
        const html = await response.text();

        this.renderFetchedProducts(html);
        this.currentPage++;
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        this.toggleLoadingSpinner(false);
        this.removeAttribute('loading');
      }
    }

    renderFetchedProducts(html) {
      const newProductGrid = new DOMParser()
        .parseFromString(html, 'text/html')
        .getElementById('product-grid');

      if (!newProductGrid?.children.length) {
        this.finalizePagination();
        return;
      }

      newProductGrid.querySelectorAll('.grid__item')
        .forEach(product => this.productList.appendChild(product));

      if (this.productList.children.length >= this.countItem) {
        this.finalizePagination();
      }


      this.bindProductLinks();
    }

    toggleLoadingSpinner(isLoading) {
      if (!this.loadMoreButton) return;

      const spinner = this.loadMoreButton.querySelector('.loading__spinner');
      this.loadMoreButton.classList.toggle('loading', isLoading);
      spinner?.classList.toggle('hidden', !isLoading);
    }

    finalizePagination() {
      this.scrollObserver?.unobserve(this);
      this.scrollObserver?.disconnect();
      this.loadMoreButton?.classList.add('visually-hidden');
    }

    disconnectedCallback() {
      this.scrollObserver?.disconnect();
      this.loadMoreButton?.removeEventListener('click', this.loadNextPage);
    }

    async restoreScrollState() {
      const savedPage = parseInt(sessionStorage.getItem('pagination-currentPage'), 10);
      const savedScrollY = parseInt(sessionStorage.getItem('pagination-scrollY'), 10);
      const savedHref = sessionStorage.getItem('pagination-itemHref');

      // Nothing to restore -> behave normally.
      if (isNaN(savedScrollY) && !savedHref) return;

      // Take over scroll restoration so the browser does not fight us on a fresh reload.
      if ('scrollRestoration' in history) history.scrollRestoration = 'manual';

      // Consume the saved state immediately so it can only ever restore once.
      sessionStorage.removeItem('pagination-currentPage');
      sessionStorage.removeItem('pagination-scrollY');
      sessionStorage.removeItem('pagination-itemHref');

      // Re-append the pages that were loaded before the user left.
      if (!isNaN(savedPage) && savedPage >= 2) {
        await this.loadPagesUpTo(savedPage);
      }

      this.stickToSavedPosition(savedHref, savedScrollY);
    }

    stickToSavedPosition(savedHref, savedScrollY) {
      const findTarget = () => {
        if (savedHref) {
          try {
            return this.productList
              ?.querySelector(`.grid__item a[href="${savedHref}"]`)
              ?.closest('.grid__item');
          } catch (e) { /* invalid selector -> fall back to scrollY */ }
        }
        return null;
      };

      const apply = () => {
        const target = findTarget();
        if (target) {
          target.scrollIntoView({ block: 'center' });
        } else if (!isNaN(savedScrollY)) {
          window.scrollTo(0, savedScrollY);
        }
      };

      apply();

      // Late-loading content (Loox reviews, Globo options, lazy images, web fonts)
      // keeps changing the page height on mobile AFTER the first restore, which is
      // what pushed the user back to the top. Keep re-asserting the position for a
      // short window while the layout is still settling, but stop the instant the
      // user interacts so we never fight their own scrolling.
      const events = ['wheel', 'touchstart', 'pointerdown', 'keydown'];
      let stopped = false;
      let ticks = 0;

      const stop = () => {
        if (stopped) return;
        stopped = true;
        clearInterval(interval);
        events.forEach(ev => window.removeEventListener(ev, stop));
      };

      const interval = setInterval(() => {
        apply();
        if (++ticks >= 40) stop(); // ~4s @ 100ms, enough for review/app widgets to load
      }, 100);

      events.forEach(ev => window.addEventListener(ev, stop, { passive: true }));
      requestAnimationFrame(apply);
    }

    async loadPagesUpTo(page) {
      let safety = 0;
      while (this.currentPage <= page && safety < 50) {
        const before = this.currentPage;
        await this.loadNextPage();
        // Stop if a page failed to load / returned nothing (no progress) to avoid an endless loop.
        if (this.currentPage === before) break;
        safety++;
      }
    }
  }

  customElements.define('custom-pagination', CustomPagination);
}
